<template>
    <transition name="hic-fade">
        <div v-if="show" class="hic-overlay" :class="{ 'hic-in-dashboard': isDashboardRoute }" role="dialog" aria-modal="true" aria-labelledby="hic-title">
            <div class="hic-box">
                <div class="hic-header">
                    <h2 id="hic-title" class="hic-title">Before you continue</h2>
                </div>

                <div class="hic-content">
                    <p class="hic-text">
                        By continuing, you agree that we may securely use your health information to coordinate your treatment with licensed providers and pharmacies, in accordance with our
                        <a :href="privacyPolicyUrl" target="_blank" rel="noopener" class="hic-link">Privacy Policy</a>.
                    </p>
                </div>

                <div class="hic-footer">
                    <button type="button" class="hic-btn-primary" @click="accept">
                        I Agree &amp; Continue
                    </button>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import { healthConsentBus, resolveHealthConsent } from '@/utils/healthConsentGate'

export default {
    name: 'HealthInfoConsentModal',
    data() {
        return {
            show: false
        }
    },
    computed: {
        isDashboardRoute() {
            return this.$route && this.$route.path && this.$route.path.startsWith('/home')
        },
        privacyPolicyUrl() {
            const homepage = process.env.VUE_APP_HOMEPAGE || 'https://newself.com'
            return `${homepage}/privacy-policy`
        }
    },
    watch: {
        show(val) {
            if (typeof document !== 'undefined') {
                document.body.style.overflow = val ? 'hidden' : ''
            }
        }
    },
    created() {
        healthConsentBus.$on('show', this.open)
        healthConsentBus.$on('hide', this.close)
    },
    beforeDestroy() {
        healthConsentBus.$off('show', this.open)
        healthConsentBus.$off('hide', this.close)
        if (typeof document !== 'undefined') {
            document.body.style.overflow = ''
        }
    },
    methods: {
        open() {
            this.show = true
        },
        close() {
            this.show = false
        },
        accept() {
            resolveHealthConsent(true)
        }
    }
}
</script>

<style scoped>
.hic-overlay {
    position: fixed;
    inset: 0;
    z-index: 2147483647;
    background-color: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
}

.hic-box {
    width: 100%;
    max-width: 480px;
    background-color: #ffffff;
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.35);
    overflow: hidden;
    font-family: inherit;
}

.hic-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 22px;
    border-bottom: 1px solid #e5e7eb;
}

.hic-title {
    font-size: 17px;
    font-weight: 600;
    color: #111827;
    margin: 0;
    line-height: 1.3;
}

.hic-content {
    padding: 22px;
    font-size: 15px;
    line-height: 1.55;
    color: #374151;
}

.hic-text {
    margin: 0;
}

.hic-link {
    color: #08466f;
    text-decoration: underline;
    font-weight: 500;
}

.hic-link:hover {
    color: #063554;
}

.hic-footer {
    padding: 16px 22px 20px 22px;
    padding-bottom: calc(20px + env(safe-area-inset-bottom, 0));
    border-top: 1px solid #f1f2f4;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.hic-btn-primary {
    width: 100%;
    padding: 14px 22px;
    border: none;
    border-radius: 14px;
    font-size: 16px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: background-color 150ms ease;
    background-color: #e9ff86;
    color: #08466f;
}

.hic-btn-primary:hover {
    background-color: #d4eb70;
}

.hic-btn-primary:active {
    background-color: #c3d966;
}

.hic-btn-primary:focus,
.hic-btn-primary:focus-visible {
    outline: none;
    box-shadow: none;
}

.hic-fade-enter-active,
.hic-fade-leave-active {
    transition: opacity 0.25s ease;
}

.hic-fade-enter-active .hic-box,
.hic-fade-leave-active .hic-box {
    transition: transform 0.25s ease;
}

.hic-fade-enter,
.hic-fade-leave-to {
    opacity: 0;
}

.hic-fade-enter .hic-box,
.hic-fade-leave-to .hic-box {
    transform: scale(0.96);
}

@media screen and (max-width: 640px) {
    .hic-overlay {
        padding: 0;
        align-items: flex-end;
    }

    .hic-box {
        max-width: 100%;
        border-radius: 20px 20px 0 0;
    }

    .hic-fade-enter .hic-box,
    .hic-fade-leave-to .hic-box {
        transform: translateY(100%);
    }

    .hic-title {
        font-size: 16px;
    }

    .hic-content {
        padding: 20px;
        font-size: 14.5px;
    }
}
</style>

<style>
[data-theme="dark"] .hic-overlay.hic-in-dashboard .hic-box {
    background-color: #181818;
}
[data-theme="dark"] .hic-overlay.hic-in-dashboard .hic-header {
    border-bottom-color: #2e2e2e;
}
[data-theme="dark"] .hic-overlay.hic-in-dashboard .hic-title {
    color: #f5f5f5;
}
[data-theme="dark"] .hic-overlay.hic-in-dashboard .hic-content {
    color: #d4d4d4;
}
[data-theme="dark"] .hic-overlay.hic-in-dashboard .hic-link {
    color: #c9e8ff;
}
[data-theme="dark"] .hic-overlay.hic-in-dashboard .hic-link:hover {
    color: #a8d4ff;
}
[data-theme="dark"] .hic-overlay.hic-in-dashboard .hic-footer {
    border-top-color: #2e2e2e;
}
</style>
